import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { API_BASE_URL } from "../../apiConfig";
import styles from "./ProfileDetailPage.module.css";
import { convertToAmPm, getProfileStatus } from "../../utils/timeUtils";
import bubbleStyles from "../../styles/Bubbles.module.css";
import ContactSection from "../ContactSection"; // Adjust path if needed
import HeaderDetails from "./HeaderDetails";
import AboutMe from "./AboutMe";

// Import each image you need from its location in src/assets/images/
import defaultThumb from "../../assets/images/default.png";

function ProfileDetailPage() {
  const { id } = useParams();
  const [profile, setProfile] = useState(null);
  const [contact, setContact] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(null);
  const imagePath = profile
    ? `https://khoneymoon-bucket.s3.us-west-1.amazonaws.com/${profile.id}`
    : "";

  const handleMediaClick = (src) => {
    const extension = src.split(".").pop().toLowerCase();
    const type = ["jpg", "jpeg", "png", "gif"].includes(extension)
      ? "image"
      : "video";
    const index = profile?.gallery?.findIndex((f) => src.endsWith(f));

    console.log("Clicked src:", src);
    console.log(
      "Expected match:",
      profile.gallery.map((f) => `${imagePath}/${f}`),
    );

    setSelectedMedia({ src, type });
    setSelectedMediaIndex(index);
  };

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/api/contact`)
      .then((response) => setContact(response.data))
      .catch((error) => console.error("Error fetching contact:", error));
  }, []);

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/api/profiles/${id}`)
      .then((response) => setProfile(response.data))
      .catch((error) => console.error("Error fetching profile:", error));
  }, [id]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (
        !selectedMedia ||
        !profile?.gallery ||
        !profile?.specialGallery?.gallery
      )
        return;

      // 🔁 Combine both galleries into one array
      const combinedGallery = [
        ...profile.gallery.map((file) => `${imagePath}/${file}`),
        ...profile.specialGallery.gallery.map((file) => `${imagePath}/${file}`),
      ];

      const total = combinedGallery.length;
      let newIndex = selectedMediaIndex;

      if (newIndex === -1) {
        newIndex = 0;
      }

      if (e.key === "Escape") {
        setSelectedMedia(null);
        setSelectedMediaIndex(null);
        return;
      }

      if (e.key === "ArrowLeft") {
        newIndex = (selectedMediaIndex - 1 + total) % total;
      } else if (e.key === "ArrowRight") {
        newIndex = (selectedMediaIndex + 1) % total;
      } else {
        return;
      }

      const newSrc = combinedGallery[newIndex];
      const extension = newSrc.split(".").pop().toLowerCase();
      const type = ["jpg", "jpeg", "png", "gif"].includes(extension)
        ? "image"
        : "video";

      setSelectedMedia({ src: newSrc, type });
      setSelectedMediaIndex(newIndex);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedMedia, selectedMediaIndex, profile]);

  const handleArrow = (direction) => {
    if (!profile?.gallery || !profile?.specialGallery?.gallery) return;

    const combinedGallery = [
      ...profile.gallery.map((file) => `${imagePath}/${file}`),
      ...profile.specialGallery.gallery.map((file) => `${imagePath}/${file}`),
    ];

    const total = combinedGallery.length;
    let newIndex = selectedMediaIndex;

    if (direction === "left") {
      newIndex = (selectedMediaIndex - 1 + total) % total;
    } else if (direction === "right") {
      newIndex = (selectedMediaIndex + 1) % total;
    }

    const newSrc = combinedGallery[newIndex];
    const extension = newSrc.split(".").pop().toLowerCase();
    const type = ["jpg", "jpeg", "png", "gif"].includes(extension)
      ? "image"
      : "video";

    setSelectedMedia({ src: newSrc, type });
    setSelectedMediaIndex(newIndex);
  };

  if (!profile) {
    return <div className="loading">Loading profile...</div>;
  }

  return (
    <>
      <div className={styles.pageWrapper}>
        <div className={styles.profileCard}>
          <div className={styles.headerSection}>
            <div className={styles.profileImageWrapper}>
              <img
                src={
                  profile.profileFullImage
                    ? `${imagePath}/${profile.profileFullImage}`
                    : defaultThumb
                }
                alt={`${profile.name}'s profile`}
                className={styles.profileImage}
              />
            </div>

            <HeaderDetails profile={profile} />
          </div>

          <AboutMe aboutMe={profile.aboutMe} />

          {profile.externalProfile &&
            profile.externalProfile.description &&
            profile.externalProfile.link &&
            profile.externalProfile.linkText && (
              <div className={styles.aboutMe}>
                <p>
                  {profile.externalProfile.description}{" "}
                  <a
                    href={profile.externalProfile.link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {profile.externalProfile.linkText}
                  </a>
                </p>
              </div>
            )}

          {profile.review.link && (
            <div className={styles.reviewBubble}>
              <a
                href={profile.review.link}
                target="_blank"
                rel="noopener noreferrer"
                className={`${bubbleStyles.bubbleStyle} ${styles.reviewBubble}`}
              >
                TER Reviews
                {profile.review.terNumber
                  ? `: ${profile.review.terNumber}`
                  : ""}
              </a>
            </div>
          )}

          {(profile.profileFullImage || profile.gallery?.length > 0) && (
            <div
              className={`${styles.gallery} ${selectedMedia ? styles.galleryDimmed : ""}`}
            >
              <div className={styles.sectionHeader}>
                <hr className={styles.sectionLine} />
                <span className={styles.sectionTitle}>Gallery</span>
                <hr className={styles.sectionLine} />
              </div>

              <div className={styles.galleryGrid}>
                {profile.gallery.map((fileName, index) => {
                  const isVideo =
                    fileName.endsWith(".mp4") || fileName.endsWith(".mov");
                  return isVideo ? (
                    <video
                      key={index}
                      src={`/videos/${fileName}`}
                      className={styles.galleryVideo}
                      muted
                      autoPlay
                      loop
                      preload="metadata"
                      onClick={() => handleMediaClick(`/videos/${fileName}`)}
                    />
                  ) : (
                    <img
                      key={index}
                      src={`${imagePath}/${fileName}`}
                      alt={`Gallery image ${index + 1}`}
                      className={styles.galleryImage}
                      onClick={() =>
                        handleMediaClick(`${imagePath}/${fileName}`)
                      }
                    />
                  );
                })}
              </div>
            </div>
          )}

          {profile.specialGallery?.gallery?.length > 0 && (
            <div
              className={`${styles.gallery} ${selectedMedia ? styles.galleryDimmed : ""}`}
            >
              <div className={styles.sectionHeader}>
                <hr className={styles.sectionLine} />
                <span className={styles.sectionTitle}>
                  {profile.specialGallery.description}
                </span>
                <hr className={styles.sectionLine} />
              </div>
              <div
                className={`${styles.galleryGrid} ${
                  profile.specialGallery.gallery.length === 1
                    ? styles.singleItem
                    : ""
                }`}
              >
                {profile.specialGallery.gallery.map((fileName, index) => {
                  const isVideo =
                    fileName.endsWith(".mp4") || fileName.endsWith(".mov");
                  return isVideo ? (
                    <video
                      key={index}
                      src={`${imagePath}/${fileName}`}
                      className={styles.galleryVideo}
                      muted
                      autoPlay
                      playsInline
                      loop
                      preload="metadata"
                      onClick={(e) => {
                        e.stopPropagation(); // prevents modal from opening
                        const video = e.currentTarget;

                        if (video.paused) {
                          video.play();
                        } else {
                          video.pause();
                        }
                      }}
                    />
                  ) : (
                    <img
                      key={index}
                      src={`${imagePath}/${fileName}`}
                      alt={`Special gallery image ${index + 1}`}
                      className={styles.galleryImage}
                      onClick={() =>
                        handleMediaClick(`${imagePath}/${fileName}`)
                      }
                    />
                  );
                })}
              </div>
            </div>
          )}

          {Array.isArray(profile.review.samples) &&
            profile.review.samples.length > 0 && (
              <div className={styles.reviewBlock}>
                <div className={styles.sectionHeader}>
                  <hr className={styles.sectionLine} />
                  <span className={styles.sectionTitle}>Review Samples</span>
                  <hr className={styles.sectionLine} />
                </div>

                <div className={styles.reviewSection}>
                  {profile.review.samples.map((review, index) => (
                    <div
                      key={index}
                      className={`${bubbleStyles.bubbleStyle} ${bubbleStyles.available} ${styles.reviewBubble}`}
                    >
                      <p className={styles.reviewText}>“{review.text}”</p>
                      {review.link && (
                        <div className={styles.reviewLinkWrapper}>
                          <a
                            href={review.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.reviewLink}
                          >
                            View Full Review ↗
                          </a>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
        </div>

        {/* 🔍 Modal */}
        {selectedMedia && (
          <div
            className={styles.modalOverlay}
            onClick={() => setSelectedMedia(null)}
          >
            <div
              className={styles.modalContent}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className={`${styles.modalButton} ${styles.close}`}
                onClick={() => setSelectedMedia(null)}
                aria-label="Close modal"
              >
                ×
              </button>
              <button
                className={`${styles.modalButton} ${styles.left}`}
                onClick={() => handleArrow("left")}
                aria-label="Previous media"
              >
                ‹
              </button>

              {selectedMedia.type === "image" ? (
                <img
                  src={selectedMedia.src}
                  alt="Selected"
                  className={styles.modalImage}
                />
              ) : selectedMedia.type === "video" ? (
                <video
                  key={selectedMedia.src}
                  src={selectedMedia.src}
                  controls
                  controlsList="nodownload"
                  autoPlay
                  className={styles.modalVideo}
                />
              ) : (
                <div>Unsupported media type</div>
              )}

              <button
                className={`${styles.modalButton} ${styles.right}`}
                onClick={() => handleArrow("right")}
                aria-label="Next media"
              >
                ›
              </button>
            </div>
          </div>
        )}

        {/* 📞 Contact Section */}
        <div className={styles.sectionBox}>
          <ContactSection contact={contact} />
        </div>
      </div>
    </>
  );
}

export default ProfileDetailPage;
